import { Fragment } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Minus, Sparkles, Rocket, Crown, ArrowRight } from "lucide-react";

import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Pricing for Hosts — Spayzo" },
      { name: "description", content: "Choose the Spayzo host plan that fits your business. Compare features across Starter, Growth and Scale." },
      { property: "og:title", content: "Pricing for Hosts — Spayzo" },
      { property: "og:description", content: "Compare Spayzo host plans and pick the one that grows your bookings." },
    ],
    links: [{ rel: "canonical", href: "https://spayzo.lovable.app/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({"@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [{"@type": "Question", "name": "Can I change plans later?", "acceptedAnswer": {"@type": "Answer", "text": "Yes \u2014 upgrade or downgrade at any time from your host dashboard. Changes are prorated."}}, {"@type": "Question", "name": "Is there a free trial?", "acceptedAnswer": {"@type": "Answer", "text": "All plans come with a 14-day free trial. No credit card required to start."}}, {"@type": "Question", "name": "What payment methods do you accept?", "acceptedAnswer": {"@type": "Answer", "text": "All major credit cards and mada. Yearly plans can also be invoiced."}}, {"@type": "Question", "name": "Do you take a booking commission?", "acceptedAnswer": {"@type": "Answer", "text": "No hidden fees \u2014 your subscription is the only cost for using Spayzo as a host."}}]}),
      },
    ],
  }),
  component: PricingPage,
});

type PlanDef = {
  id: "starter" | "growth" | "scale";
  monthly: number;
  yearly: number;
  icon: React.ComponentType<{ className?: string }>;
  recommended?: boolean;
  featureCount: number;
};

const PLANS: PlanDef[] = [
  { id: "starter", monthly: 19, yearly: 190, icon: Sparkles, featureCount: 4 },
  { id: "growth", monthly: 49, yearly: 490, icon: Rocket, recommended: true, featureCount: 5 },
  { id: "scale", monthly: 129, yearly: 1290, icon: Crown, featureCount: 5 },
];

type CompareRow = { key: string; values: [string | boolean, string | boolean, string | boolean] };
type CompareGroup = { titleKey: string; rows: CompareRow[] };

const COMPARE: CompareGroup[] = [
  {
    titleKey: "listings",
    rows: [
      { key: "spaces", values: ["3", "15", "unlimitedVal"] },
      { key: "photos", values: ["10", "30", "unlimitedVal"] },
      { key: "branding", values: [false, true, true] },
      { key: "multi", values: [false, false, true] },
    ],
  },
  {
    titleKey: "bookings",
    rows: [
      { key: "unlimited", values: [true, true, true] },
      { key: "instant", values: [false, true, true] },
      { key: "offers", values: [false, true, true] },
      { key: "subs", values: [false, true, true] },
    ],
  },
  {
    titleKey: "insights",
    rows: [
      { key: "basic", values: [true, true, true] },
      { key: "advanced", values: [false, true, true] },
      { key: "export", values: [false, true, true] },
      { key: "api", values: [false, false, true] },
    ],
  },
  {
    titleKey: "support",
    rows: [
      { key: "email", values: [true, true, true] },
      { key: "priority", values: [false, true, true] },
      { key: "manager", values: [false, false, true] },
      { key: "sla", values: [false, false, true] },
    ],
  },
];

function PricingPage() {
  const { t } = useTranslation();
  const [cycle, setCycle] = useState<"monthly" | "yearly">("monthly");

  const unlimitedLabel = t("pricing.rows.unlimitedVal");

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main className="mx-auto max-w-7xl px-4 pb-24 pt-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/10">{t("pricing.badge")}</Badge>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("pricing.title")}</h1>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">{t("pricing.subtitle")}</p>

          <div className="mt-8 flex justify-center">
            <Tabs value={cycle} onValueChange={(v) => setCycle(v as typeof cycle)}>
              <TabsList className="rounded-xl">
                <TabsTrigger value="monthly" className="rounded-lg">{t("pricing.monthly")}</TabsTrigger>
                <TabsTrigger value="yearly" className="rounded-lg">{t("pricing.yearly")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {PLANS.map((p) => {
            const Icon = p.icon;
            const price = cycle === "monthly" ? p.monthly : Math.round(p.yearly / 12);
            const features = t(`pricing.plans.${p.id}.features`, { returnObjects: true }) as string[];
            return (
              <Card
                key={p.id}
                className={cn(
                  "relative flex flex-col rounded-2xl p-6 shadow-soft transition",
                  p.recommended ? "border-primary/40 ring-2 ring-primary/30" : "border-border/60",
                )}
              >
                {p.recommended && (
                  <Badge className="absolute -top-3 start-6 rounded-full bg-[#5958F3] text-white hover:bg-[#5958F3]">
                    {t("pricing.mostPopular")}
                  </Badge>
                )}
                <div className="flex items-center gap-3">
                  <div className={cn("grid h-11 w-11 place-items-center rounded-xl", p.recommended ? "bg-[#5958F3] text-white" : "bg-primary/10 text-primary")}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-display text-lg font-bold truncate">{t(`pricing.plans.${p.id}.name`)}</p>
                    <p className="text-xs text-muted-foreground truncate">{t(`pricing.plans.${p.id}.blurb`)}</p>
                  </div>
                </div>

                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold tracking-tight"><Riyal /> {price}</span>
                  <span className="text-sm text-muted-foreground">{t("pricing.perMonth")}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {cycle === "yearly"
                    ? t("pricing.billedYearly", { amount: p.yearly })
                    : t("pricing.orYearly", { amount: p.yearly })}
                </p>

                <ul className="mt-5 space-y-2.5">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <span className={cn("mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full", p.recommended ? "bg-[#5958F3] text-white" : "bg-primary/10 text-primary")}>
                        <Check className="h-2.5 w-2.5" />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  <Button
                    asChild
                    className={cn("h-11 w-full rounded-xl", p.recommended ? "bg-[#5958F3] text-white hover:bg-[#4847d1]" : "")}
                    variant={p.recommended ? "default" : "outline"}
                  >
                    <Link to="/partner">
                      {t(`pricing.plans.${p.id}.cta`)} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
                    </Link>
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        <section className="mt-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold tracking-tight">{t("pricing.compareTitle")}</h2>
            <p className="mt-3 text-sm text-muted-foreground">{t("pricing.compareSub")}</p>
          </div>

          <Card className="mt-10 overflow-hidden rounded-2xl border-border/60 shadow-soft">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-surface">
                  <tr>
                    <th className="w-[36%] px-6 py-4 text-start font-display text-base font-bold">{t("pricing.featuresCol")}</th>
                    {PLANS.map((p) => (
                      <th key={p.id} className="px-6 py-4 text-start font-display text-base font-bold">
                        <div className="flex items-center gap-2">
                          {t(`pricing.plans.${p.id}.name`)}
                          {p.recommended && (
                            <Badge className="rounded-full bg-[#5958F3] text-white hover:bg-[#5958F3]">{t("pricing.popular")}</Badge>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs font-normal text-muted-foreground"><Riyal /> {cycle === "monthly" ? p.monthly : Math.round(p.yearly / 12)}{t("pricing.perMonth")}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((group) => (
                    <Fragment key={group.titleKey}>
                      <tr className="bg-muted/30">
                        <td colSpan={4} className="px-6 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                          {t(`pricing.groups.${group.titleKey}`)}
                        </td>
                      </tr>
                      {group.rows.map((row) => (
                        <tr key={row.key} className="border-t border-border/60">
                          <td className="px-6 py-3.5 text-foreground">{t(`pricing.rows.${row.key}`)}</td>
                          {row.values.map((v, i) => (
                            <td key={i} className="px-6 py-3.5">
                              {typeof v === "boolean" ? (
                                v ? (
                                  <span className="inline-grid h-5 w-5 place-items-center rounded-full bg-[#5958F3]/10 text-[#5958F3]">
                                    <Check className="h-3 w-3" />
                                  </span>
                                ) : (
                                  <span className="inline-grid h-5 w-5 place-items-center rounded-full bg-muted text-muted-foreground">
                                    <Minus className="h-3 w-3" />
                                  </span>
                                )
                              ) : (
                                <span className="text-foreground">{v === "unlimitedVal" ? unlimitedLabel : v}</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>

        <section className="mt-20 grid gap-6 md:grid-cols-2">
          {(t("pricing.faq", { returnObjects: true }) as { q: string; a: string }[]).map((f) => (
            <Card key={f.q} className="rounded-2xl border-border/60 p-6 shadow-soft">
              <p className="font-display text-base font-bold">{f.q}</p>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </Card>
          ))}
        </section>

        <section className="mt-16 overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-[#5958F3]/10 via-background to-background p-8 shadow-soft sm:p-12">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="max-w-xl">
              <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("pricing.ctaTitle")}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t("pricing.ctaBody")}</p>
            </div>
            <Button asChild className="h-12 rounded-xl bg-[#5958F3] px-6 text-white hover:bg-[#4847d1]">
              <Link to="/partner">
                {t("pricing.ctaBtn")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
